import {
    IFirstStageSiteGroupData, ISecondStageSiteGroupData
} from '@/dto/operationalPlanSummary';
import {
    DataCategory,
    ComponentData,
    IRequestsData,
    IExpandedState,
} from '.';
import { RequestsData } from './RequestsData';
import { MtrRecord } from './MtrRecord';
import { ResourceRecord } from './ResourceRecord';
import { ResourceStackRecord } from './ResourceStackRecord';
import { localize } from '@/core/filters';

export class SiteGroup
    implements IExpandedState {

    public static fromFirstPart(siteGroupData: IFirstStageSiteGroupData, parent: ComponentData): SiteGroup {

        if (!siteGroupData) { throw new Error('siteGroupData is not set'); }

        const siteGroup = new SiteGroup(siteGroupData.alias, parent);
        siteGroup.name = siteGroupData.name;

        const stocks = siteGroupData.stocks;
        if (stocks) {
            siteGroup.stockWeight = stocks.initial;
            siteGroup.incomeRelocationsWeight = stocks.income;
            siteGroup.lossRelocationsWeight = stocks.outcome;
        }

        const incomeConsumptions = siteGroupData.incomeConsumptions;
        if (incomeConsumptions) {
            siteGroup.incomeRequests.countWithoutDelay = incomeConsumptions.satisfiedCount;
            siteGroup.incomeRequests.countPostponed = incomeConsumptions.postponedCount;
            siteGroup.incomeRequests.countWithDelay = incomeConsumptions.unsatisfiedCount;
        }

        const incomeMtr = siteGroupData.incomeMtrChartData;
        if (incomeMtr) {
            siteGroup.income = incomeMtr
                .map(x => MtrRecord.fromDto(x));
        }

        return siteGroup;
    }

    public isExpanded: boolean = false;
    public alias: string;
    public name: string = '';

    public incomeRelocationsWeight: number = 0;
    public lossRelocationsWeight: number = 0;
    public stockWeight: number = 0;

    public incomeRequests: IRequestsData = new RequestsData();
    public outcomeRequests: IRequestsData = new RequestsData();

    public income: MtrRecord[] = [];
    public outcome: MtrRecord[] = [];
    public get unit(): string {
        return (this.income[0]!.unitByRelocations &&
                this.income[0]!.unitByRelocations === this.income[0].unitByConsumptions &&
                this.income[0]!.unitByRelocations !== '') ?
                this.income[0]!.unitByRelocations :
                this.defaultUnitsConversion;
    }

    public vehicleChartRecords: ResourceRecord[] = [];
    public craneChartRecords: ResourceRecord[] = [];

    public resourceStackRecords: ResourceStackRecord[] = [];

    public readonly parent: ComponentData;

    constructor(alias: string, parent: ComponentData) {
        if (!alias || alias.length <= 0) { throw new Error('alias is not set'); }
        if (!parent) { throw new Error('parent is not set'); }

        this.alias = alias;
        this.parent = parent;
    }

    public get selectedDataCategory(): DataCategory {
        return this.parent.selectedDataCategory;
    }

    public parseSecondStageData(data: ISecondStageSiteGroupData): void {

        const outcomeConsumptions = data.outcomeConsumptions;
        if (outcomeConsumptions) {
            this.outcomeRequests.countWithoutDelay = outcomeConsumptions.satisfiedCount;
            this.outcomeRequests.countPostponed = outcomeConsumptions.postponedCount;
            this.outcomeRequests.countWithDelay = outcomeConsumptions.unsatisfiedCount;
        }

        const outcomeMtr = data.outcomeMtrChartData;
        if (outcomeMtr) {
            this.outcome = outcomeMtr
                .map(x => MtrRecord.fromDto(x));
        }

        const vehicleChartData = data.vehicleChartData;
        if (vehicleChartData) {
            this.vehicleChartRecords = vehicleChartData
                .map(x => ResourceRecord.fromDto(x));
        }

        const craneChartData = data.craneChartData;
        if (craneChartData) {
            this.craneChartRecords = craneChartData
                .map(x => ResourceRecord.fromDto(x));
        }

        const resourceStackRecords = data.resourceStackRecords;
        if (resourceStackRecords) {
            this.resourceStackRecords = resourceStackRecords
                .map(x => ResourceStackRecord.fromDto(x));
        }
    }

    private get defaultUnitsConversion() {
        let unit = localize('OPS_MeasureKgShort');

        if ((this.incomeRelocationsWeight >= 1000 || this.incomeRelocationsWeight === 0) &&
            (this.lossRelocationsWeight >= 1000 || this.lossRelocationsWeight === 0) &&
            (this.stockWeight >= 1000 || this.stockWeight === 0)) {
                this.incomeRelocationsWeight = this.incomeRelocationsWeight / 1000;
                this.lossRelocationsWeight = this.lossRelocationsWeight / 1000;
                this.stockWeight = this.stockWeight / 1000;

                unit = localize('OPS_MeasureTnShort');
            }

        return unit;
    }
}
